import { useEffect, useState } from 'react';
import { PlayCircle, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/**
 * Урок от Славы в начале «Практики самоподдержки» (14.10.2026).
 *
 * Слава: «мне нужно записать голосовой урок или видео-инструкцию внутри» — человек сначала
 * слышит, зачем практика и как сформулировать фразы, и только потом пишет свои.
 *
 * Файл лежит в публичной корзине `meditations` по пути `self-support/lesson.*`.
 * Пока файла нет, блок не показывается вовсе — выкладывать код раньше записи безопасно.
 * Поддержаны видео (mp4) и голос (m4a, mp3); первым ищется видео.
 */

const CANDIDATES: { path: string; kind: 'video' | 'audio' }[] = [
  { path: 'self-support/lesson.mp4', kind: 'video' },
  { path: 'self-support/lesson.m4a', kind: 'audio' },
  { path: 'self-support/lesson.mp3', kind: 'audio' },
];

export function SelfSupportLesson() {
  const [found, setFound] = useState<{ url: string; kind: 'video' | 'audio' } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      for (const c of CANDIDATES) {
        const url = supabase.storage.from('meditations').getPublicUrl(c.path).data.publicUrl;
        try {
          const r = await fetch(url, { method: 'HEAD' });
          if (r.ok) {
            if (alive) setFound({ url, kind: c.kind });
            return;
          }
        } catch { /* нет сети или файла — пробуем следующий */ }
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!found) return null;

  return (
    <div className="rounded-2xl border border-emerald-glow/30 bg-emerald-glow/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full p-4 flex items-center gap-3 text-left"
      >
        <PlayCircle size={28} className="text-emerald-glow shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Как это работает — урок от Славы</div>
          <div className="text-[11px] text-ink-dim mt-0.5">Послушай перед первой записью: как выбрать слова, которые поддержат именно тебя</div>
        </div>
        <ChevronDown size={16} className={`text-ink-dim shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4">
          {found.kind === 'video' ? (
            <video src={found.url} controls playsInline preload="metadata" className="w-full rounded-xl bg-black" />
          ) : (
            <audio src={found.url} controls preload="metadata" className="w-full" />
          )}
        </div>
      )}
    </div>
  );
}
